import { toastService } from "../screens/notificationPage/ToastContainer";

/**
 * Error type to toast type mapping
 */
const errorTypeMap = {
  ERR_VALIDATION: "warning",
  ERR_UNAUTHORIZED: "error",
  ERR_INVALID_CREDENTIALS: "error",
  ERR_FORBIDDEN: "error",
  ERR_NOT_FOUND: "warning",
  ERR_DUPLICATE: "warning",
  ERR_RATE_LIMIT: "warning",
  ERR_INTERNAL: "error",
  ERR_SERVICE_UNAVAILABLE: "error",
  ERR_UNKNOWN: "error",
};

/**
 * Custom error messages for specific error codes
 */
const errorMessages = {
  ERR_INVALID_CREDENTIALS: "Invalid username or password. Please try again.",
  ERR_TOKEN_EXPIRED: "Your session has expired. Please log in again.",
  ERR_UNAUTHORIZED: "You need to be logged in to access this feature.",
  ERR_FORBIDDEN: "You don't have permission to perform this action.",
  ERR_VALIDATION: "Please check your information and try again.",
  ERR_NOT_FOUND: "The requested resource was not found.",
  ERR_DUPLICATE_USERNAME: "This username is already taken.",
  ERR_RATE_LIMIT: "Too many requests. Please try again later.",
  ERR_INTERNAL: "An unexpected error occurred. Please try again later.",
  ERR_SERVICE_UNAVAILABLE:
    "Service is currently unavailable. Please try again later.",
};

/**
 * Main error handler function for API errors
 *
 * @param {Object} error - The error object from RTK Query
 * @param {Object} options - Options for handling the error
 * @param {boolean} options.showToast - Whether to show a toast notification
 * @param {Object} options.setErrors - State setter for form errors
 * @param {Function} options.onAuthError - Callback for authentication errors
 * @param {string} options.toastPosition - Position for toast notifications
 * @returns {Object} Processed error information
 */
export const handleApiError = (error, options = {}) => {
  const {
    showToast = true,
    setErrors = null,
    onAuthError = null,
    toastPosition = "top-right",
  } = options;

  // Default error information
  const errorInfo = {
    message: "An unexpected error occurred",
    code: "ERR_UNKNOWN",
    status: error?.status || 500,
    fieldErrors: {},
    details: {},
  };

  // Extract error data from RTK Query error response
  if (error && error.data) {
    errorInfo.message = error.data.message || errorInfo.message;
    errorInfo.code = error.data.errorCode || errorInfo.code;
    errorInfo.details = error.data.details || {};

    // Extract field-level errors if available
    if (error.data.fieldErrors) {
      errorInfo.fieldErrors = error.data.fieldErrors;
    }
  }

  // Use custom message if available
  const customMessage = errorMessages[errorInfo.code];
  if (customMessage) {
    errorInfo.displayMessage = customMessage;
  } else {
    errorInfo.displayMessage = errorInfo.message;
  }

  // Handle authentication errors
  if (
    [401, 403].includes(errorInfo.status) ||
    ["ERR_UNAUTHORIZED", "ERR_INVALID_TOKEN", "ERR_TOKEN_EXPIRED"].includes(
      errorInfo.code
    )
  ) {
    if (onAuthError && typeof onAuthError === "function") {
      onAuthError(errorInfo);
    }
  }

  // Update form errors if setErrors function is provided
  if (setErrors && typeof setErrors === "function") {
    const formErrors = { general: errorInfo.displayMessage };

    // Add field-specific errors if available
    if (Object.keys(errorInfo.fieldErrors).length > 0) {
      Object.keys(errorInfo.fieldErrors).forEach((field) => {
        formErrors[field] = errorInfo.fieldErrors[field];
      });
    }

    setErrors(formErrors);
  }

  // Show toast notification if enabled
  if (showToast) {
    const toastType = errorTypeMap[errorInfo.code] || "error";
    toastService[toastType](errorInfo.displayMessage, {
      position: toastPosition,
    });
  }

  return errorInfo;
};

/**
 * Helper function to extract field errors from validation error
 *
 * @param {Object} error - The error object from RTK Query
 * @returns {Object} Field errors object
 */
export const extractFieldErrors = (error) => {
  const fieldErrors = {};

  if (error?.data?.fieldErrors) {
    return error.data.fieldErrors;
  }

  if (error?.data?.details?.fields) {
    return error.data.details.fields;
  }

  return fieldErrors;
};

/**
 * Helper function to check if error is an authentication error
 *
 * @param {Object} error - The error object from RTK Query
 * @returns {boolean} True if it's an auth error
 */
export const isAuthError = (error) => {
  if (!error) return false;

  const status = error.status || (error.data && error.data.statusCode);
  const code = error.data && error.data.errorCode;

  return (
    [401, 403].includes(status) ||
    ["ERR_UNAUTHORIZED", "ERR_INVALID_TOKEN", "ERR_TOKEN_EXPIRED"].includes(
      code
    )
  );
};
